<template>
  <div>
    <div class="spinner-container" key="spinner" v-if="loading && !error">
      <pn-spinner color="blue" size="large"></pn-spinner>
    </div>

    <order-details-modal-component
      :orderData="{ order: selectedOrder }"
      @order-detail-modal-closed="modalClosed"
      @refreshList="getOrders"
    >
    </order-details-modal-component>

    <transition name="fade" mode="out-in">
      <div v-if="(!loading && emptyOrders) || error">
        <p class="subtext">
          {{ $t('W2P_MY_ORDERS_EMPTY_SUBTEXT') }}
        </p>
        <div class="footer">
          <img
            src="@/assets/images/letters/orders/my-mailouts-end.svg"
            alt="end illustration image"
          />
          <h2>{{ $t('W2P_MY_ORDERS_EMPTY_TITLE') }}</h2>
          <p class="empty-msg">{{ $t('W2P_MY_ORDERS_EMPTY_MSG') }}</p>
          <pn-button-container size="medium">
            <button @click="$router.push('/')">
              {{ $t('W2P_V2_NAVIGATION_CREATE') }}
            </button>
          </pn-button-container>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <div class="table-component" v-if="!loading && !emptyOrders">
        <p class="subtext">
          {{ $t('W2P_MY_ORDERS_SUBTEXT') }}
        </p>
        <pn-input-container label="Label">
          <input
            type="search"
            @change="search = $event.target.value"
            v-model="search"
            :placeholder="$t('SEARCH')"
          />
        </pn-input-container>
        <div class="order-table-content" key="table">
          <pn-table bordered>
            <table>
              <thead>
                <tr>
                  <th v-for="item in compHeaders" :key="item.id">
                    <div class="column-wrapper">
                      {{ $t(item.title) }}
                      <pn-icon
                        symbol="unfold"
                        color="blue700"
                        size="medium"
                        @click="sortOrders(item.id)"
                        solid
                      >
                      </pn-icon>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredOrders"
                  :key="item.orderId"
                  @click="open(item)"
                >
                  <td>{{ item.orderDate | formatDate }}</td>
                  <td>
                    <p class="bold">{{ item.product.productName[lang] }}</p>
                    <p>{{ item.recipients }} recipients</p>
                  </td>
                  <td>{{ item.name }}</td>
                  <td
                    v-if="
                      !item.product.ownMaterial ||
                        (item.product.ownMaterial &&
                          item.status === 'CANCELLED')
                    "
                  >
                    <pn-icon
                      :symbol="statuses[item.status].symbol"
                      :color="statuses[item.status].background"
                      size="medium"
                      solid
                    >
                    </pn-icon>
                    {{ $t(statuses[item.status].text) }}
                  </td>
                  <td
                    v-if="
                      item.product.ownMaterial && item.status === 'UNPROCESSED'
                    "
                  >
                    {{ $t('W2P_NOT_AVAILABLE_MESSAGE') }}
                  </td>
                  <td>
                    <button class="order-link">{{ item.orderId }}</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </pn-table>
          <div class="footer">
            <img
              src="@/assets/images/letters/orders/my-mailouts-end.svg"
              alt="end illustration image"
            />
            <p>{{ $t('W2P_MY_ORDERS_END') }}</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import OrderDetailsModalComponent from '@/components/order-details-modal/OrderDetailsModalComponent.vue'
import * as orderHelper from '@/helpers/orderHelper.js'
import orderDetails from '@/components/order-details-modal/orderDetails.json'

export default {
  components: {
    OrderDetailsModalComponent
  },
  data: () => ({
    loading: true,
    error: false,
    search: '',
    orders: [],
    sortOrder: 'DESC',
    statuses: orderDetails.statuses,
    selectedOrder: null
  }),
  computed: {
    ...mapState(['accessToken', 'lang']),
    compHeaders() {
      return [
        { title: 'DATE', id: 'orderDate' },
        { title: 'TYPE', id: `product.productName.${this.lang}` },
        { title: 'NAME', id: 'name' },
        { title: 'STATUS', id: 'status' },
        { title: 'ORDER_ID', id: 'orderId' }
      ]
    },
    filteredOrders() {
      let regex = new RegExp(this.search, 'i')
      return this.orders.filter(order => {
        if (typeof order.product.productName === 'string') {
          return
        }
        return (
          order.orderDate.match(regex) ||
          order.product.productName[this.lang].match(regex) ||
          order.name.match(regex) ||
          order.status.match(regex) ||
          order.orderId.match(regex)
        )
      })
    },
    emptyOrders() {
      return this.orders.length <= 0
    }
  },
  mounted() {
    this.getOrders()
  },
  methods: {
    getOrders() {
      axios({
        method: 'get',
        url:
          process.env.VUE_APP_W2P_API_BASE_URL +
          '/orders/orders?filterKey=status&filterValue=PLACED&filterExclude=true',
        headers: { authorization: this.accessToken }
      })
        .then(response => {
          this.orders = response.data.orders.map(orderHelper.mapOrder)
          this.sortOrders('orderDate')
          this.loading = false
        })
        .catch(() => (this.error = true))
    },
    toggleSortOrder() {
      if (this.sortOrder === 'ASC') {
        return 'DESC'
      }

      return 'ASC'
    },
    sortOrders(key) {
      key === 'orderDate' ? this.sortByDate() : this.sortByString(key)

      this.sortOrder = this.toggleSortOrder()
    },
    sortByDate() {
      this.orders = this.orders.sort((a, b) => {
        const dateA = new Date(a.orderDate)
        const dateB = new Date(b.orderDate)

        return this.sortOrder === 'DESC' ? dateB - dateA : dateA - dateB
      })
    },
    fetchMulti(obj, key) {
      let result = obj
      const parts = key.split('.')

      for (let i = 0; i < parts.length; i++) {
        if (!result[parts[i]]) {
          return undefined
        }

        result = result[parts[i]]
      }

      return result
    },
    sortByString(key) {
      this.orders.sort((a, b) => {
        a = this.fetchMulti(a, key)
        b = this.fetchMulti(b, key)
        const varA = typeof a === 'string' ? a.toUpperCase() : a
        const varB = typeof b === 'string' ? b.toUpperCase() : b

        const comparison = varA > varB ? 1 : -1

        return this.sortOrder === 'DESC' ? comparison * -1 : comparison
      })
    },
    open(order) {
      this.selectedOrder = order
      document.getElementById('order-details-modal').open()
    },
    modalClosed() {
      this.selectedOrder = null
    }
  }
}
</script>

<style lang="scss" scoped>
.subtext {
  margin: 1.5rem 0;
  color: $gray700;
}

.table-component {
  .order-link {
    padding: 0;
    border: none;
    background: none;
    font-size: 1em;
    color: $blue900;
  }
  pn-input-container {
    width: 100%;
    max-width: 42rem;
    margin: 3rem 0 2rem;
  }

  pn-table {
    max-width: 85em;

    tr {
      td:first-child {
        white-space: nowrap;
      }

      td {
        word-break: break-word;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  td:nth-last-child(-n + 3) {
    white-space: nowrap;
  }

  .spinner-container {
    text-align: center;
  }
}

.footer {
  padding-top: 2em;
  text-align: center;

  h2 {
    color: $gray900;
    margin-top: 1em;
  }

  p {
    color: $gray400;
    margin-top: 1em;

    &.empty-msg {
      color: $gray700;
      font-size: 0.875em;
    }
  }

  pn-button-container {
    margin-top: 1.7em;
    min-width: 14.5em;
  }
}

@media screen and (max-width: 75em) {
  pn-input-container {
    max-width: 100%;
  }

  th:nth-last-child(-n + 3),
  td:nth-last-child(-n + 3) {
    display: none;
  }

  td:nth-child(2) {
    border-right: 1px solid $gray50;
  }
}
</style>
